import { DsmGrid, DsmLoadingIndicator } from "@dsm-dcs/design-system-react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { useState, useCallback, useMemo } from "react";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import { AnimalType } from "../../../../graphql/types";
import DsmButtonControlGroup from "../helpers/DsmButtonControlGroup";
import {
  accessSampleDataDialogStyles,
  processAndStageStylesV2,
} from "../../../../_metronic/layout";
import useAccessSampleData, {
  ANIMAL_TYPES_WITH_SAMPLE_DATA,
  getRegionsForAnimalType,
  Region,
  requiresRegionSelection,
} from "./useAccessSampleData";
import featureFlags from "../../utils/featureFlags";
import { CSSClassesList } from "../../helpers/helperTypes";

export enum SampleType {
  FARM = "FARM",
  FEED = "FEED",
}

const REGION_TO_ISO: Partial<Record<Region, string>> = {
  [Region.BRAZIL]: "BR",
  [Region.FRANCE]: "FR",
  [Region.NETHERLANDS]: "NL",
  [Region.SPAIN]: "ES",
};

interface AccessSampleDataProps {
  customerID: string;
  isShowPostLogin?: boolean;
  onClose?: () => void;
}

const AccessSampleData = ({
  customerID,
  onClose,
  isShowPostLogin = false,
}: AccessSampleDataProps) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const dialogClasses: CSSClassesList = accessSampleDataDialogStyles();

  const [sampleDataType, setSampleDataType] = useState<SampleType | "">("");
  const [animalType, setAnimalType] = useState<AnimalType | "">("");
  const [regions, setRegions] = useState<Region[]>([]);
  const [doNotShowAgain, setDoNotShowAgain] = useState<boolean>(false);

  const { saving, errorMessage, saveSampleDataRequest, clearError } =
    useAccessSampleData({ customerID, onClose });

  const isAnimalType = (value: string): value is AnimalType =>
    Object.values(AnimalType).includes(value as AnimalType);

  const availableRegionsForSelectedAnimal = useMemo(
    () => (animalType ? getRegionsForAnimalType(animalType) : undefined),
    [animalType],
  );

  const handleRegionBoxChanges = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      const value = event.target.value as Region;

      if (value === Region.ALL) {
        setRegions(checked ? availableRegionsForSelectedAnimal ?? [] : []);
        clearError();
        return;
      }

      setRegions((prev) => {
        const withoutAllAndCurrent = prev.filter(
          (r) => r !== Region.ALL && r !== value,
        );

        if (!checked) {
          return withoutAllAndCurrent;
        }

        const newRegions = [...withoutAllAndCurrent, value];

        const nonAllRegions =
          availableRegionsForSelectedAnimal?.filter((r) => r !== Region.ALL) ||
          [];

        const hasAllOption = availableRegionsForSelectedAnimal?.includes(
          Region.ALL,
        );

        if (hasAllOption && newRegions.length === nonAllRegions.length) {
          return [...newRegions, Region.ALL];
        }

        return newRegions;
      });

      clearError();
    },
    [availableRegionsForSelectedAnimal, clearError],
  );

  const getRegionLabel = (regionValue: Region) => {
    if (regionValue === Region.ALL) {
      return intl.formatMessage({
        id: "GENERAL.SELECT_ALL",
      });
    }

    if (regionValue === Region.NETHERLANDS) {
      return intl.formatMessage({
        id: "REQUEST.ACCESS.SAMPLE.DATA.CHECKBOX.NETHERLANDS",
      });
    }

    const isoCode = REGION_TO_ISO[regionValue];
    if (isoCode) {
      return intl.formatMessage({
        id: `SUSTELL.GEOGRAPHY.${isoCode}`,
      });
    }

    return regionValue;
  };

  const canSubmit = useMemo(() => {
    if (isShowPostLogin && doNotShowAgain && !sampleDataType && !animalType) {
      return true;
    }

    if (!sampleDataType || !animalType) {
      return false;
    }

    if (sampleDataType === SampleType.FEED && featureFlags.feedV2Routing) {
      return false;
    }

    if (requiresRegionSelection(animalType) && regions.length === 0) {
      return false;
    }

    return true;
  }, [
    isShowPostLogin,
    doNotShowAgain,
    sampleDataType,
    animalType,
    regions.length,
  ]);

  const handleSave = async () => {
    await saveSampleDataRequest({
      sampleDataType: sampleDataType !== "" ? sampleDataType : undefined,
      animalType: animalType !== "" ? animalType : undefined,
      regions,
      doNotShowAgain: isShowPostLogin ? doNotShowAgain : undefined,
    });
  };

  const handleCancel = () => {
    setSampleDataType("");
    setAnimalType("");
    setRegions([]);
    setDoNotShowAgain(false);
    clearError();
    onClose?.();
  };

  return (
    <DsmGrid className={classes.dsmGridOneColumnNoRowgap}>
      <div style={{ fontWeight: 500 }}>
        {intl.formatMessage({
          id: "REQUEST.ACCESS.SAMPLE.DATA.SUB.TITLE",
        })}
      </div>
      <div>
        {intl.formatMessage({
          id: "REQUEST.ACCESS.SAMPLE.DATA.DESCRIPTION",
        })}
      </div>

      {isShowPostLogin && (
        <div style={{ paddingTop: "10px" }}>
          {intl.formatMessage({
            id: "REQUEST.ACCESS.SAMPLE.DATA.DESCRIPTION.SUB",
          })}
        </div>
      )}

      <FormControl>
        <FormLabel
          className={dialogClasses.formLabel}
          style={{ color: "#1b1b1b" }}
        >
          {intl.formatMessage({
            id: "REQUEST.ACCESS.SAMPLE.DATA.SELECT.FARM_OR_FEED",
          })}
        </FormLabel>

        <RadioGroup
          value={sampleDataType}
          onChange={(e) => {
            setSampleDataType(e.target.value as SampleType);
            clearError();
          }}
          aria-required
        >
          <FormControlLabel
            value="FARM"
            control={<Radio />}
            label={intl.formatMessage({ id: "GENERAL.FARM" })}
            className={
              sampleDataType === SampleType.FARM
                ? dialogClasses.formControlLabelSelected
                : dialogClasses.formControlLabel
            }
          />
          <FormControlLabel
            value="FEED"
            control={<Radio />}
            disabled={featureFlags.feedV2Routing}
            label={
              <span>
                {intl.formatMessage({ id: "GENERAL.FEED" })}
                {featureFlags.feedV2Routing && (
                  <span style={{ color: "#E51F22", marginLeft: 2 }}>
                    {intl.formatMessage({
                      id: "REQUEST.ACCESS.SAMPLE.DATA.FEED_DISABLED_WARNING",
                    })}
                  </span>
                )}
              </span>
            }
            className={
              sampleDataType === SampleType.FEED
                ? dialogClasses.formControlLabelSelected
                : dialogClasses.formControlLabel
            }
          />
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel
          className={dialogClasses.formLabel}
          style={{ color: "#1b1b1b" }}
        >
          {intl.formatMessage({
            id: "REQUEST.ACCESS.SAMPLE.DATA.SELECT.ANIMALTYPE",
          })}
        </FormLabel>

        <RadioGroup
          row
          value={animalType}
          onChange={(e) => {
            if (!isAnimalType(e.target.value)) return;
            setAnimalType(e.target.value);
            setRegions([]);
            clearError();
          }}
          aria-label={intl.formatMessage({
            id: "REQUEST.ACCESS.SAMPLE.DATA.SELECT.ANIMALTYPE",
          })}
          aria-required
        >
          {ANIMAL_TYPES_WITH_SAMPLE_DATA.map((animalTypeOption) => (
            <FormControlLabel
              key={animalTypeOption}
              value={animalTypeOption}
              label={intl.formatMessage({
                id: `SUSTELL.ANIMAL.TYPE.${animalTypeOption}`,
              })}
              control={<Radio />}
              style={{ width: "160px", paddingLeft: "30px" }}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {availableRegionsForSelectedAnimal &&
        availableRegionsForSelectedAnimal.length > 0 && (
          <FormControl>
            <FormLabel
              className={dialogClasses.formLabel}
              style={{ color: "#1b1b1b" }}
            >
              {intl.formatMessage({
                id: "REQUEST.ACCESS.SAMPLE.DATA.SELECT.REGION",
              })}
            </FormLabel>

            <DsmGrid
              style={{ display: "inline-flex" }}
              className={classes.dsmGridOneColumnNoRowgap}
            >
              {availableRegionsForSelectedAnimal.map((region) => (
                <FormControlLabel
                  key={region}
                  value={region}
                  label={getRegionLabel(region)}
                  control={<Checkbox />}
                  checked={regions.includes(region)}
                  onChange={handleRegionBoxChanges}
                  style={{
                    width:
                      animalType === AnimalType.BroilerV2 ? "250px" : "150px",
                    paddingLeft: "10px",
                  }}
                />
              ))}
            </DsmGrid>
          </FormControl>
        )}

      {isShowPostLogin && (
        <FormControlLabel
          checked={doNotShowAgain}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDoNotShowAgain(e.target.checked)
          }
          control={<Checkbox />}
          label={intl.formatMessage({
            id: "REQUEST.ACCESS.SAMPLE.DATA.DO_NOT_SHOW_CHECKBOX",
          })}
        />
      )}

      {errorMessage && errorMessage.length > 0 && (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          style={{ color: "#E51F22", fontSize: "14px", paddingLeft: "30px" }}
        >
          {errorMessage}
        </div>
      )}

      <DsmButtonControlGroup
        cancelHandler={handleCancel}
        saveHandler={canSubmit && !saving ? handleSave : undefined}
        saveLabel={intl.formatMessage({
          id: "REQUEST.ACCESS.SAMPLE.DATA.SAVE.BUTTON",
        })}
      />

      {saving && (
        <DsmLoadingIndicator
          style={{
            textAlign: "center",
            position: "fixed",
            left: "50%",
            top: "50%",
          }}
        />
      )}
    </DsmGrid>
  );
};

export default AccessSampleData;
